
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getResellerOrderById, approveOrder, rejectOrder, holdOrder } from '@/utils/resellerOrderService';
import { ResellerOrder } from '@/types/resellerOrder';
import { useToast } from '@/hooks/use-toast';
import MasterLicenseSettingsLayout from '@/components/masterlicense/settings/MasterLicenseSettingsLayout';
import { useMasterLicense } from '@/hooks/useMasterLicense';
import { 
  Card, 
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useForm } from 'react-hook-form';
import { Check, X, Clock, ArrowLeft, Save } from 'lucide-react';
import { formatDate } from '@/lib/utils';

interface ResellerOrderDetailProps {
  isEditMode?: boolean;
}

const ResellerOrderDetail: React.FC<ResellerOrderDetailProps> = ({ isEditMode = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState<ResellerOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const { handleLogout, user } = useMasterLicense();
  const [activeTab, setActiveTab] = useState('details');

  const form = useForm({
    defaultValues: {
      businessName: '',
      businessEmail: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      notes: '',
    }
  });

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const orderData = await getResellerOrderById(id);
        if (orderData) {
          setOrder(orderData);
          form.reset({
            businessName: orderData.businessName,
            businessEmail: orderData.businessEmail,
            contactName: orderData.contactName,
            contactEmail: orderData.contactEmail,
            contactPhone: orderData.contactPhone,
            notes: orderData.notes || '',
          });
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        toast({
          title: 'Error',
          description: 'Failed to load order details',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [id, form, toast]);

  const handleApprove = async () => {
    if (!order) return;
    
    setIsProcessing(true);
    try {
      await approveOrder(order.id, form.getValues().notes);
      toast({
        title: 'Order Approved',
        description: `Order ${order.orderNumber} has been approved.`
      });
      navigate('/master/settings');
    } catch (error) {
      console.error('Error approving order:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve order',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!order) return;
    
    setIsProcessing(true);
    try {
      await rejectOrder(order.id, form.getValues().notes);
      toast({
        title: 'Order Rejected',
        description: `Order ${order.orderNumber} has been rejected.`
      });
      navigate('/master/settings');
    } catch (error) {
      console.error('Error rejecting order:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject order',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleHold = async () => {
    if (!order) return;
    
    setIsProcessing(true);
    try {
      await holdOrder(order.id, form.getValues().notes);
      toast({
        title: 'Order On Hold',
        description: `Order ${order.orderNumber} has been placed on hold.`
      });
      navigate('/master/settings');
    } catch (error) {
      console.error('Error holding order:', error);
      toast({
        title: 'Error',
        description: 'Failed to place order on hold',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const onSubmit = (data: any) => {
    console.log('Form submitted:', data);
    toast({
      title: 'Order Updated',
      description: 'The order has been successfully updated.'
    });
    navigate('/master/settings');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      case 'hold':
        return <Badge className="bg-orange-100 text-orange-800">On Hold</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  return (
    <MasterLicenseSettingsLayout
      isLoading={false}
      handleLogout={handleLogout}
      userRole={user?.role || 'Master Admin'}
      activeTab="settings"
    >
      <div className="container mx-auto py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/master/settings')}
            className="flex items-center"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Settings
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? 'Edit Order' : 'Order Details'}
            {order && ` - ${order.orderNumber}`}
          </h1>
          {order && getStatusBadge(order.status)}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : order ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Order Information</CardTitle>
                  <CardDescription>
                    Order details and customer information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-4">
                      <TabsTrigger value="details">Order Details</TabsTrigger>
                      <TabsTrigger value="business">Business Info</TabsTrigger>
                      <TabsTrigger value="notes">Notes</TabsTrigger>
                    </TabsList>
                    
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)}>
                        <TabsContent value="details">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h3 className="font-medium mb-2">Order Number</h3>
                              <p className="text-gray-700">{order.orderNumber}</p>
                            </div>
                            <div>
                              <h3 className="font-medium mb-2">Order Date</h3>
                              <p className="text-gray-700">{formatDate(order.orderDate)}</p>
                            </div>
                            <div>
                              <h3 className="font-medium mb-2">Plan</h3>
                              <p className="text-gray-700">{order.plan}</p>
                            </div>
                            <div>
                              <h3 className="font-medium mb-2">Payment Amount</h3>
                              <p className="text-gray-700">${order.paymentAmount}</p>
                            </div>
                            <div>
                              <h3 className="font-medium mb-2">Payment Method</h3>
                              <p className="text-gray-700">{order.paymentMethod}</p>
                            </div>
                            <div>
                              <h3 className="font-medium mb-2">Domain</h3>
                              <p className="text-gray-700">{order.domain}</p>
                            </div>
                            {order.transactionId && (
                              <div>
                                <h3 className="font-medium mb-2">Transaction ID</h3>
                                <p className="text-gray-700">{order.transactionId}</p>
                              </div>
                            )}
                            {order.scheduledActivationTime && (
                              <div>
                                <h3 className="font-medium mb-2">Scheduled Activation</h3>
                                <p className="text-gray-700">{formatDate(order.scheduledActivationTime)}</p>
                              </div>
                            )}
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="business">
                          <div className="space-y-4">
                            <FormField
                              control={form.control}
                              name="businessName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Business Name</FormLabel>
                                  <FormControl>
                                    <Input {...field} disabled={!isEditMode} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="businessEmail"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Business Email</FormLabel>
                                  <FormControl>
                                    <Input {...field} disabled={!isEditMode} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="contactName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Contact Name</FormLabel>
                                  <FormControl>
                                    <Input {...field} disabled={!isEditMode} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="contactEmail"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Contact Email</FormLabel>
                                  <FormControl>
                                    <Input {...field} disabled={!isEditMode} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="contactPhone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Contact Phone</FormLabel>
                                  <FormControl>
                                    <Input {...field} disabled={!isEditMode} />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="notes">
                          <FormField
                            control={form.control}
                            name="notes"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Order Notes</FormLabel>
                                <FormControl>
                                  <Textarea
                                    {...field}
                                    disabled={!isEditMode && order.status !== 'pending'}
                                    rows={5}
                                    placeholder="Add notes about this order..."
                                  />
                                </FormControl>
                                <FormDescription>
                                  These notes will be associated with any actions performed on this order.
                                </FormDescription>
                              </FormItem>
                            )}
                          />
                        </TabsContent>

                        {isEditMode && (
                          <div className="mt-6 flex justify-end">
                            <Button 
                              type="submit" 
                              disabled={isProcessing}
                              className="flex items-center"
                            >
                              <Save className="mr-2 h-4 w-4" />
                              Save Changes
                            </Button>
                          </div>
                        )}
                      </form>
                    </Form>
                  </Tabs>
                </CardContent>
                
                {!isEditMode && order.status === 'pending' && (
                  <CardFooter className="flex justify-end gap-3 border-t pt-4">
                    <Button 
                      variant="outline" 
                      onClick={handleHold} 
                      disabled={isProcessing}
                      className="flex items-center"
                    >
                      <Clock className="mr-2 h-4 w-4" />
                      Put on Hold
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleReject} 
                      disabled={isProcessing}
                      className="flex items-center"
                    >
                      <X className="mr-2 h-4 w-4" />
                      Reject
                    </Button>
                    <Button 
                      onClick={handleApprove} 
                      disabled={isProcessing}
                      className="flex items-center"
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Approve
                    </Button>
                  </CardFooter>
                )}
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Order Number:</span>
                      <span className="font-medium">{order.orderNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Order Date:</span>
                      <span>{formatDate(order.orderDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span>{getStatusBadge(order.status)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan:</span>
                      <span>{order.plan}</span>
                    </div>
                    <div className="pt-3 border-t">
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span>${order.paymentAmount}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex-col gap-3">
                  {!isEditMode && (
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => navigate(`/master/settings/orders/${order.id}/edit`)}
                    >
                      Edit Order
                    </Button>
                  )}
                  {isEditMode && (
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => navigate(`/master/settings/orders/${order.id}`)}
                    >
                      View Details
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium mb-2">Order not found</h3>
            <p className="text-gray-500">The order you're looking for doesn't exist or has been removed.</p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/master/settings')}
              className="mt-4"
            >
              Return to Settings
            </Button>
          </div>
        )}
      </div>
    </MasterLicenseSettingsLayout>
  );
};

export default ResellerOrderDetail;
